/**
 * Universal Page Transition & Homepage Intro Loader Engine
 *
 * Requirements:
 * 1. Full-screen cinematic Namaste Intro Loader is active on HOMEPAGE ONLY, and only once per session.
 * 2. Big Rectangle Transition is UNIVERSAL for every page, post, and content load across the project.
 * 3. Starts at full opacity, enlarges size, slowly reduces opacity to transparent, revealing content.
 * 4. Intercepts internal link navigation to provide seamless cinematic page transitions throughout.
 * Architecture: Nirajan <https://nbk.com.np>
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

export class IntroLoader {
  constructor(onComplete) {
    this.loader = document.getElementById('nbk-intro-loader');
    this.transitionFrame = document.getElementById('nbk-transition-frame');
    this.onComplete = onComplete;

    this.headerAvatar = document.querySelector('.nbk-branding a');
    this.contentTarget =
      document.getElementById('intro') ||
      document.getElementById('hero') ||
      document.getElementById('main-content') ||
      document.querySelector('main');

    // Check session memory so users aren't trapped in loader on internal navigation
    const hasViewedIntro =
      typeof window !== 'undefined' &&
      window.sessionStorage &&
      window.sessionStorage.getItem('nbk_intro_viewed') === 'true';

    const isBot =
      typeof window !== 'undefined' &&
      window.navigator &&
      /Lighthouse|PageSpeed|HeadlessChrome|GTmetrix/i.test(window.navigator.userAgent);
    const isReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Fast-path bypass for audit bots or reduced motion users
    if (isBot || isReducedMotion) {
      if (this.loader) {
        this.loader.style.display = 'none';
      }
      if (this.transitionFrame) {
        this.transitionFrame.style.display = 'none';
      }
      if (this.headerAvatar) {
        gsap.set(this.headerAvatar, { opacity: 1, visibility: 'visible' });
      }
      document.body.style.overflow = '';
      if (typeof this.onComplete === 'function') {
        this.onComplete();
      }
      this.initPageLinkTransitions();
      return;
    }

    // 1. If NOT homepage, or if loader already completed in this session:
    if (!this.loader || hasViewedIntro) {
      if (this.loader) {
        this.loader.style.display = 'none';
      }
      if (this.headerAvatar) {
        gsap.set(this.headerAvatar, { opacity: 1, visibility: 'visible' });
      }

      // Execute Universal Big Rectangle Transition for every page/post load
      this.triggerBigRectangleTransition();
      this.initPageLinkTransitions();
      return;
    }

    // 2. Otherwise, run the full Homepage Cinematic Intro
    this.avatarContainer = this.loader.querySelector('.loader-avatar-container');
    this.spinnerCircle = this.loader.querySelector('.loader-spinner-circle');
    this.namasteIcon = this.loader.querySelector('.loader-namaste-icon');
    this.avatarImg = this.loader.querySelector('.loader-avatar-img');
    this.avatarAura = this.loader.querySelector('.loader-avatar-aura');
    this.spinnerSvg = this.loader.querySelector('.loader-spinner-svg');

    this.spotlight = this.loader.querySelector('.fx-spotlight:not(.fx-spotlight-sub)');
    this.subtext = this.loader.querySelector('.loader-subtext');
    this.loaderTexts = this.loader.querySelector('.loader-texts');

    this.initHomepageIntro();
    this.initPageLinkTransitions();
  }

  initHomepageIntro() {
    // Lock scroll during intro animation
    document.body.style.overflow = 'hidden';

    // Hide target header avatar initially until the intro avatar docks into it
    if (this.headerAvatar) {
      gsap.set(this.headerAvatar, { opacity: 0, visibility: 'hidden' });
    }

    // Set initial states
    gsap.set(this.namasteIcon, { opacity: 1, scale: 1 });
    gsap.set(this.avatarImg, { opacity: 0, scale: 0.85 });
    if (this.spotlight) {
      gsap.set(this.spotlight, { opacity: 0, y: 12 });
    }
    if (this.subtext) {
      gsap.set(this.subtext, { opacity: 0, y: 10 });
    }
    gsap.set(this.spinnerCircle, { strokeDashoffset: 628.3 });

    if (this.transitionFrame) {
      gsap.set(this.transitionFrame, { opacity: 0, visibility: 'hidden' });
    }

    // Run master choreography timeline
    this.playHomepageSequence();
  }

  playHomepageSequence() {
    const tl = gsap.timeline({
      defaults: { ease: 'power3.out' },
    });

    // 1. Namaste Icon display time (600ms) with gentle breathing scale
    tl.to(this.namasteIcon, {
      scale: 1.05,
      duration: 0.6,
      ease: 'sine.inOut',
    });

    // 2. Namaste Spotlight text reveals smoothly while the spotlight sweeps across
    if (this.spotlight) {
      tl.to(
        this.spotlight,
        {
          opacity: 1,
          y: 0,
          duration: 0.55,
          ease: 'power2.out',
        },
        '-=0.2',
      );
    }

    // 3. Subtext reveals smoothly right under Namaste
    if (this.subtext) {
      tl.to(
        this.subtext,
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: 'power2.out',
        },
        '-=0.2',
      );
    }

    // 4. Pause for a moment to let the user observe the sweeping spotlight effect
    tl.to({}, { duration: 0.7 });

    // 4. Morph / replace Namaste icon with the Vector Avatar (Always pure circular)
    tl.to(this.namasteIcon, {
      opacity: 0,
      scale: 0.7,
      rotate: -12,
      duration: 0.65,
      ease: 'power2.inOut',
    }).to(
      this.avatarImg,
      {
        opacity: 1,
        scale: 1,
        rotate: 0,
        duration: 0.75,
        ease: 'back.out(1.2)',
      },
      '-=0.5',
    );

    // 5. Circular loading spinner traces around avatar with color pulse (0% -> 100%)
    tl.to(
      this.spinnerCircle,
      {
        strokeDashoffset: 0,
        duration: 1.15,
        ease: 'power2.inOut',
      },
      '-=0.1',
    ).to(
      this.avatarAura,
      {
        scale: 1.35,
        opacity: 1,
        duration: 0.55,
        yoyo: true,
        repeat: 1,
        ease: 'sine.inOut',
      },
      '-=1.0',
    );

    // 6. Fade out loader texts and spinner
    tl.to(
      this.loaderTexts || this.spotlight,
      {
        opacity: 0,
        y: -20,
        duration: 0.45,
        ease: 'power2.in',
      },
      '+=0.1',
    ).to(
      this.spinnerSvg,
      {
        opacity: 0,
        scale: 0.95,
        duration: 0.3,
      },
      '-=0.35',
    );

    // 6. Circular avatar flies smoothly from center to the Header Avatar coordinates
    tl.add(() => {
      this.flyAvatarToHeader();
    }, '+=0.1');
  }

  flyAvatarToHeader() {
    if (!this.headerAvatar || !this.avatarContainer) {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.setItem('nbk_intro_viewed', 'true');
      }
      this.triggerBigRectangleTransition();
      return;
    }

    const targetRect = this.headerAvatar.getBoundingClientRect();
    const currentRect = this.avatarContainer.getBoundingClientRect();

    const deltaX =
      targetRect.left + targetRect.width / 2 - (currentRect.left + currentRect.width / 2);
    const deltaY =
      targetRect.top + targetRect.height / 2 - (currentRect.top + currentRect.height / 2);
    const scaleFactor = targetRect.width / currentRect.width;

    const flyTl = gsap.timeline({
      onComplete: () => {
        // Dock into header avatar
        gsap.set(this.headerAvatar, { opacity: 1, visibility: 'visible' });
        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.setItem('nbk_intro_viewed', 'true');
        }
        // Trigger the universal big rectangle transition into content
        this.triggerBigRectangleTransition();
      },
    });

    // Circular avatar flies directly to header, staying pure circle at all times
    flyTl.to(this.avatarContainer, {
      x: deltaX,
      y: deltaY,
      scale: scaleFactor,
      duration: 0.85,
      ease: 'power3.inOut',
    });
  }

  /**
   * Universal Big Rectangle Page Transition (Applies to all pages/posts)
   */
  triggerBigRectangleTransition() {
    const transitionTl = gsap.timeline({
      onComplete: () => {
        if (this.loader) {
          this.loader.style.display = 'none';
        }
        if (this.transitionFrame) {
          this.transitionFrame.style.display = 'none';
        }
        document.body.style.overflow = '';
        ScrollTrigger.refresh();
        if (typeof this.onComplete === 'function') {
          this.onComplete();
        }
      },
    });

    if (this.transitionFrame) {
      const maskHole = this.transitionFrame.querySelector('.transition-mask-hole');
      const maskBorder = this.transitionFrame.querySelector('.transition-mask-border');

      // 1. Set transition frame visible
      this.transitionFrame.style.display = 'block';
      gsap.set(this.transitionFrame, {
        opacity: 1,
        visibility: 'visible',
      });

      // 2. Ensure initial SVG mask hole attributes are strictly rounded (rx: 40, ry: 40)
      if (maskHole) {
        gsap.set(maskHole, {
          attr: {
            x: '6%',
            y: '10%',
            width: '88%',
            height: '80%',
            rx: 40,
            ry: 40,
          },
        });
      }

      // 3. SVG border strictly matches mask hole coordinates and geometry (0 subpixel divergence)
      if (maskBorder) {
        gsap.set(maskBorder, {
          opacity: 1,
          attr: {
            x: '6%',
            y: '10%',
            width: '88%',
            height: '80%',
            rx: 40,
            ry: 40,
          },
        });
      }

      // 4. Fade out loader curtain cleanly if it exists
      if (this.loader) {
        transitionTl.to(this.loader, {
          opacity: 0,
          duration: 0.25,
          ease: 'power2.out',
        });
      }

      // 5. Hold framed view for 0.35s, then expand the inside transparent window to fullwidth over 2.25s
      // Zero pointed corners: the SVG mask hole and border expand together from rx: 40 to rx: 0 smoothly!
      if (maskBorder) {
        transitionTl.to(
          maskBorder,
          {
            attr: {
              x: '0%',
              y: '0%',
              width: '100%',
              height: '100%',
              rx: 0,
              ry: 0,
            },
            opacity: 0,
            duration: 2.25,
            ease: 'power2.inOut',
          },
          '+=0.35',
        );
      }

      if (maskHole) {
        transitionTl.to(
          maskHole,
          {
            attr: {
              x: '0%',
              y: '0%',
              width: '100%',
              height: '100%',
              rx: 0,
              ry: 0,
            },
            duration: 2.25,
            ease: 'power2.inOut',
          },
          '<',
        );
      }
    }
  }

  /**
   * Seamless Internal Link Navigation Transitions
   */
  initPageLinkTransitions() {
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (!link) return;

      const href = link.getAttribute('href');
      // Only intercept internal links that navigate away (not anchors like #hero, not mailto, not external)
      if (
        !href ||
        href.startsWith('#') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        href.startsWith('javascript:') ||
        link.getAttribute('target') === '_blank'
      ) {
        return;
      }

      try {
        const url = new window.URL(href, window.location.origin);
        if (url.origin === window.location.origin && url.pathname !== window.location.pathname) {
          e.preventDefault();
          this.transitionOutAndNavigate(url.href);
        }
      } catch {
        // Fallback standard navigation
      }
    });
  }

  transitionOutAndNavigate(targetUrl) {
    if (!this.transitionFrame) {
      window.location.href = targetUrl;
      return;
    }

    const maskHole = this.transitionFrame.querySelector('.transition-mask-hole');
    const maskBorder = this.transitionFrame.querySelector('.transition-mask-border');

    this.transitionFrame.style.display = 'block';
    gsap.set(this.transitionFrame, { opacity: 1, visibility: 'visible' });

    if (maskHole) {
      gsap.set(maskHole, {
        attr: {
          x: '0%',
          y: '0%',
          width: '100%',
          height: '100%',
          rx: 0,
          ry: 0,
        },
      });
    }

    if (maskBorder) {
      gsap.set(maskBorder, {
        opacity: 0,
        attr: {
          x: '0%',
          y: '0%',
          width: '100%',
          height: '100%',
          rx: 0,
          ry: 0,
        },
      });
    }

    const outTl = gsap.timeline({
      onComplete: () => {
        window.location.href = targetUrl;
      },
    });

    if (maskBorder) {
      outTl.to(maskBorder, {
        opacity: 1,
        attr: {
          x: '6%',
          y: '10%',
          width: '88%',
          height: '80%',
          rx: 40,
          ry: 40,
        },
        duration: 0.85,
        ease: 'power2.inOut',
      });
    }

    if (maskHole) {
      outTl.to(
        maskHole,
        {
          attr: {
            x: '6%',
            y: '10%',
            width: '88%',
            height: '80%',
            rx: 40,
            ry: 40,
          },
          duration: 0.85,
          ease: 'power2.inOut',
        },
        '<',
      );
    }
  }
}
